// Lobby settings for Guess in an online room: the category (picked by the host) and how it works.
import type { GuessCategoryInfo } from "@shared/guess/api";
import type { GuessSettings } from "@shared/games/guess";
import type { Lang } from "@shared/types";
import { useEffect, useState } from "react";
import { Section } from "../../components/Controls";
import { Clapperboard, Flag, ImageIcon, Mic } from "../../components/Icon";
import { fmt } from "../../i18n";
import { play } from "../../lib/sfx";
import { guessBackend } from "./backend";
import { useGuessDict } from "./i18n";

export function GuessSettingsPanel({
  settings: s,
  editable,
  set,
  lang,
}: {
  settings: GuessSettings;
  editable: boolean;
  set(patch: Partial<GuessSettings>): void;
  lang: Lang;
}) {
  const t = useGuessDict();
  const [cats, setCats] = useState<GuessCategoryInfo[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    guessBackend
      .categories(lang)
      .then((c) => {
        if (!alive) return;
        setCats(c);
        // the default category may not be playable on this server: pick the first one that is
        if (editable && c.length && !c.some((x) => x.slug === s.category)) set({ category: c[0].slug });
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  return (
    <div className="space-y-6">
      <p className="flex items-start gap-3 rounded-[20px] bg-ground-2 p-4 text-ink-2">
        <Mic size={20} className="mt-0.5 shrink-0 text-guess" />
        <span>{t.online.lobbyNote}</span>
      </p>
      <Section title={t.online.catLabel}>
        {!editable && <p className="-mt-1 mb-3 text-sm text-ink-3">{t.online.hostPicks}</p>}
        {failed && <p className="text-bad">{t.catLoadError}</p>}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {(cats ?? []).map((c) => {
            const on = s.category === c.slug;
            return (
              <button
                key={c.slug}
                type="button"
                disabled={!editable}
                onClick={() => {
                  if (on) return;
                  play("pop");
                  set({ category: c.slug });
                }}
                className="tile flex flex-col items-center gap-2 p-2.5 text-center disabled:cursor-default"
                style={{ ["--b" as string]: on ? "var(--color-guess)" : "var(--color-ground-3)", opacity: !editable && !on ? 0.55 : 1 }}
                aria-pressed={on}
              >
                <span className="grid aspect-[4/3] w-full place-items-center overflow-hidden rounded-[16px] bg-ground-2">
                  {c.cover ? <img src={c.cover} alt="" className="h-full w-full object-cover" loading="lazy" /> : <Glyph kind={c.kind} />}
                </span>
                <span className="text-sm leading-tight font-extrabold">{c.name}</span>
                <span className="text-xs text-ink-3">{fmt(t.catCount, { n: c.count })}</span>
              </button>
            );
          })}
          {!cats && !failed && Array.from({ length: 6 }, (_, i) => <div key={i} className="aspect-[4/5] animate-pulse rounded-[24px] bg-ground-2" />)}
        </div>
      </Section>
      <div className="surface-2 p-4">
        <p className="font-extrabold">{t.rulesText}</p>
      </div>
    </div>
  );
}

/** Phones: the chosen category right in the lobby (the full settings live in the sheet). */
export function GuessLobbyCard({ settings, lang, isHost, onEdit }: { settings: GuessSettings; lang: Lang; isHost: boolean; onEdit(): void }) {
  const t = useGuessDict();
  const [cats, setCats] = useState<GuessCategoryInfo[] | null>(null);
  useEffect(() => {
    let alive = true;
    guessBackend
      .categories(lang)
      .then((c) => alive && setCats(c))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [lang]);
  const cat = cats?.find((c) => c.slug === settings.category) ?? null;
  return (
    <div className="surface flex items-center gap-4 p-4">
      <span className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-[18px] bg-ground-2">
        {cat?.cover ? <img src={cat.cover} alt="" className="h-full w-full object-cover" /> : <Glyph kind={cat?.kind ?? "image"} />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-ink-3">{t.online.catLabel}</div>
        <div className="line-clamp-2 text-lg leading-tight font-extrabold">{cat?.name ?? "…"}</div>
      </div>
      {isHost && (
        <button type="button" className="btn btn-sm btn-soft" onClick={onEdit}>
          {t.online.change}
        </button>
      )}
    </div>
  );
}

function Glyph({ kind }: { kind: string }) {
  const Icon = kind === "emoji" ? Clapperboard : kind === "flag" ? Flag : ImageIcon;
  return <Icon size={44} strokeWidth={1.6} className="text-guess" />;
}
